import { Link } from "@tanstack/react-router";
import { ArrowLeft, FileStack, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GC_TYPE_LABELS } from "@/components/gc-line-items/GcLineItemDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type GcLineItemTemplate,
  type GcLineItemTemplateRowInput,
  useCreateGcLineItemTemplate,
  useDeleteGcLineItemTemplate,
  useGcLineItemTemplates,
  useUpdateGcLineItemTemplate,
} from "@/hooks/use-gc-line-item-templates";
import type { GcLineItemBillingMethod, GcLineItemType } from "@/hooks/use-gc-line-items";

// Internal row shape with a stable key for React list rendering.
// Stripped before submit (payload uses GcLineItemTemplateRowInput).
type FormRow = GcLineItemTemplateRowInput & { _key: string };

interface FormState {
  name: string;
  description: string;
  isDefault: boolean;
  isActive: boolean;
  rows: FormRow[];
}

function newRowKey(): string {
  return crypto.randomUUID();
}

const emptyForm: FormState = {
  name: "",
  description: "",
  isDefault: false,
  isActive: true,
  rows: [],
};

export function GcLineItemTemplatesPage() {
  const { data, isLoading, isError, error, refetch } = useGcLineItemTemplates();
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<GcLineItemTemplate | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const remove = useDeleteGcLineItemTemplate();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const templates = data ?? [];

  async function handleDelete(id: string) {
    try {
      await remove.mutateAsync(id);
      toast.success("Template deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setConfirmDeleteId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="GC Line Item Templates"
        description="Reusable GC-owned cost baselines applied to new projects"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Admin
              </Link>
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setEditOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </div>
        }
      />

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileStack className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold">No templates yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Define a reusable set of GC line items that can be applied to new projects.
            </p>
            <Button
              className="mt-4"
              onClick={() => {
                setEditing(null);
                setEditOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <Card key={t.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    {t.name}
                    {t.isDefault ? (
                      <Badge variant="secondary" className="text-[10px]">
                        <Star className="mr-1 h-3 w-3" /> Default
                      </Badge>
                    ) : null}
                    {!t.isActive ? (
                      <Badge variant="outline" className="text-[10px]">
                        Inactive
                      </Badge>
                    ) : null}
                  </CardTitle>
                  {t.description ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>
                  ) : null}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(t);
                      setEditOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteId(t.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xs text-muted-foreground">
                  {t.rows.length} line item{t.rows.length === 1 ? "" : "s"}
                </div>
                <ul className="mt-2 divide-y rounded border text-sm">
                  {t.rows.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                      <span className="truncate">
                        <span className="font-medium">{r.label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {GC_TYPE_LABELS[r.lineType]}
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {r.billingMethod === "fixed_lump_sum"
                          ? "Fixed"
                          : r.billingMethod === "percent_of_hard_costs"
                            ? `% hard costs${
                                r.percentOfHardCosts
                                  ? ` (${(Number(r.percentOfHardCosts) * 100).toFixed(2)}%)`
                                  : ""
                              }`
                            : "Manual / period"}
                        {!r.ownerFacing ? " · internal" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateDialog open={editOpen} onOpenChange={setEditOpen} existing={editing} />

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
        title="Delete template?"
        description="Projects already using this template are not affected. You can't undo this."
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteId) handleDelete(confirmDeleteId);
        }}
        variant="destructive"
      />
    </div>
  );
}

function TemplateDialog({
  open,
  onOpenChange,
  existing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: GcLineItemTemplate | null;
}) {
  const create = useCreateGcLineItemTemplate();
  const update = useUpdateGcLineItemTemplate();
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        name: existing.name,
        description: existing.description ?? "",
        isDefault: existing.isDefault,
        isActive: existing.isActive,
        rows: existing.rows.map((r) => ({
          _key: r.id,
          lineType: r.lineType,
          label: r.label,
          billingMethod: r.billingMethod,
          percentOfHardCosts: r.percentOfHardCosts,
          ownerFacing: r.ownerFacing,
          retainageApplies: r.retainageApplies,
          retentionPercentOverride: r.retentionPercentOverride,
          costCodeId: r.costCodeId,
          displayOrder: r.displayOrder,
          requiresReasonPerDraw: r.requiresReasonPerDraw,
          notes: r.notes,
        })),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, existing]);

  function addRow() {
    setForm((prev) => ({
      ...prev,
      rows: [
        ...prev.rows,
        {
          _key: newRowKey(),
          lineType: "general_conditions",
          label: GC_TYPE_LABELS.general_conditions,
          billingMethod: "fixed_lump_sum",
          ownerFacing: true,
          retainageApplies: true,
          requiresReasonPerDraw: false,
        },
      ],
    }));
  }

  function updateRow(idx: number, patch: Partial<GcLineItemTemplateRowInput>) {
    setForm((prev) => {
      const rows = prev.rows.slice();
      const current = rows[idx];
      if (!current) return prev;
      rows[idx] = { ...current, ...patch };
      return { ...prev, rows };
    });
  }

  function removeRow(idx: number) {
    setForm((prev) => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    // Drop the internal _key field before sending to the API.
    const payloadRows: GcLineItemTemplateRowInput[] = form.rows.map(({ _key, ...rest }) => rest);
    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          input: {
            name: form.name.trim(),
            description: form.description || null,
            isDefault: form.isDefault,
            isActive: form.isActive,
            rows: payloadRows,
          },
        });
        toast.success("Template updated");
      } else {
        await create.mutateAsync({
          name: form.name.trim(),
          description: form.description || null,
          isDefault: form.isDefault,
          isActive: form.isActive,
          rows: payloadRows,
        });
        toast.success("Template created");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save template");
    }
  }

  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription>
            Templates seed baseline rows when applied to a project. Amounts stay project-specific.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Name</Label>
              <Input
                id="tpl-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="flex items-end gap-3 pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.isDefault}
                  onCheckedChange={(v) => setForm({ ...form, isDefault: v === true })}
                />
                Default for new projects
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.isActive}
                  onCheckedChange={(v) => setForm({ ...form, isActive: v === true })}
                />
                Active
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-desc">Description</Label>
            <Textarea
              id="tpl-desc"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Line items</h3>
              <Button type="button" size="sm" variant="outline" onClick={addRow}>
                <Plus className="mr-1 h-3 w-3" />
                Add row
              </Button>
            </div>
            {form.rows.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                No rows yet. Add the GC baselines this template should seed.
              </p>
            ) : (
              <div className="space-y-2">
                {form.rows.map((row, idx) => (
                  <div
                    key={row._key}
                    className="grid grid-cols-12 gap-2 rounded border bg-muted/30 p-2 text-sm"
                  >
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-muted-foreground">Type</Label>
                      <Select
                        value={row.lineType}
                        onValueChange={(v) => {
                          const lineType = v as GcLineItemType;
                          updateRow(idx, {
                            lineType,
                            label:
                              lineType === "custom" || row.label !== GC_TYPE_LABELS[row.lineType]
                                ? row.label
                                : GC_TYPE_LABELS[lineType],
                            billingMethod:
                              lineType === "gc_fee" ? "percent_of_hard_costs" : row.billingMethod,
                            requiresReasonPerDraw:
                              lineType === "owner_contingency" || lineType === "gc_contingency"
                                ? true
                                : row.requiresReasonPerDraw,
                            retainageApplies:
                              lineType === "owner_contingency" || lineType === "gc_contingency"
                                ? false
                                : row.retainageApplies,
                          });
                        }}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(GC_TYPE_LABELS) as GcLineItemType[]).map((k) => (
                            <SelectItem key={k} value={k}>
                              {GC_TYPE_LABELS[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-muted-foreground">Label</Label>
                      <Input
                        className="h-8"
                        value={row.label}
                        onChange={(e) => updateRow(idx, { label: e.target.value })}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-muted-foreground">Billing</Label>
                      <Select
                        value={row.billingMethod}
                        onValueChange={(v) =>
                          updateRow(idx, {
                            billingMethod: v as GcLineItemBillingMethod,
                          })
                        }
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed_lump_sum">Fixed lump sum</SelectItem>
                          <SelectItem value="percent_of_hard_costs">% hard costs</SelectItem>
                          <SelectItem value="manual_per_period">Manual / period</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      {row.billingMethod === "percent_of_hard_costs" ? (
                        <>
                          <Label className="text-[10px] uppercase text-muted-foreground">
                            % (0–1)
                          </Label>
                          <Input
                            className="h-8"
                            type="number"
                            step="0.0001"
                            min="0"
                            max="1"
                            value={row.percentOfHardCosts ?? ""}
                            onChange={(e) =>
                              updateRow(idx, {
                                percentOfHardCosts: e.target.value || null,
                              })
                            }
                          />
                        </>
                      ) : null}
                    </div>
                    <div className="col-span-1 flex items-end justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(idx)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="col-span-12 flex flex-wrap gap-4 pt-1 text-xs">
                      <label className="flex items-center gap-1">
                        <Checkbox
                          checked={row.ownerFacing ?? true}
                          onCheckedChange={(v) => updateRow(idx, { ownerFacing: v === true })}
                        />
                        Owner-facing
                      </label>
                      <label className="flex items-center gap-1">
                        <Checkbox
                          checked={row.retainageApplies ?? true}
                          onCheckedChange={(v) => updateRow(idx, { retainageApplies: v === true })}
                        />
                        Retainage applies
                      </label>
                      <label className="flex items-center gap-1">
                        <Checkbox
                          checked={row.requiresReasonPerDraw ?? false}
                          onCheckedChange={(v) =>
                            updateRow(idx, { requiresReasonPerDraw: v === true })
                          }
                        />
                        Require draw reason
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !form.name.trim()}>
              {existing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
